import { SyncOutlined } from '@ant-design/icons';
import { Alert as AntdAlert } from 'antd';
import ExternalRoutes from 'common/navigation/routes/external-routes';
import ErrorSummary from 'common/publish-steps/containers/error-summary-container';
import isEqual from 'lodash.isequal';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { compose } from 'redux';
import styled from 'styled-components';
import { Button, notification } from 'ui';
import { withRouter } from 'utils/withRouter';
import { errorSummaryStyles } from '../../publish-steps/components/error-summary';
import {
  clearNotifications,
  showNotification,
} from '../containers/error-actions';
import GenericError from './generic-error';

const Alert = styled(AntdAlert)`
  margin: 10px 0;
  .alert-button {
    margin-left: 20px;
  }
`;

// sometimes we have to add !important to override antd styles specificity
const ErrorButton = styled(Button)`
  background-color: ${p => p.theme.colors ? p.theme.colors.themeSecondary : '#6f7e95'} !important;
  color: #fff !important;
  border-color: transparent !important;
  text-transform: uppercase;
  font-weight: ${p => p.theme.fontWeight ? p.theme.fontWeight.bolder : 600};
  margin: 5px 5px 0 0;
  &:hover {
    background-color: #fff !important;
    color: ${p => p.theme.colors ? p.theme.colors.themeSecondary : '#6f7e95'} !important;
  }
`;

const Description = styled.div`
  word-wrap: break-word;
  white-space: pre-wrap;
`;

const ErrorList = styled.ul`
  list-style: disc;
  padding-left: 1rem;
  margin-bottom: 0.3rem;
`;

const Section = styled.div`
  margin-bottom: 10px;
`;

export function getErrorOptions({ status, message, errors, title, isWarning, intl }) {
  const createCustomOptions = (defaultMessage, defaultDescription, key) => {
    // check for errors sent from api and display them
    if (errors && errors.length > 0) {
      return {
        message,
        key: key || 'error',
        onClose: () => notification.destroy(key || 'error'),
        description: <ErrorList>
          {errors?.map((err, i) =>
            <li key={i}>{err}</li>,
          )}
        </ErrorList>,
        type: 'error',
      };
    }

    const btn = isWarning ?
      <ErrorButton onClick={() => notification.destroy(key)}>
        {intl.formatMessage({ id: 'common.OK' })}
      </ErrorButton>
      : null;
    // otherwise display generic message (based on Steven's implementation)
    return {
      message: defaultMessage,
      description: defaultDescription,
      className: 'extended-notification',
      type: isWarning ? 'warning' : 'error',
      key: key || 'error',
      btn,
      duration: btn ? 0 : 5,
      onClose: () => notification.destroy(key || 'error'),
    };
  };

  if (status === 400) {
    return createCustomOptions(
      title || 'Bad request',
      message || 'The endpoint was invalid',
      'badRequest',
    );
  } else if (status === 401) {
    return {
      message: 'Unauthorized',
      className: 'extended-notification',
      description: message || 'Please login',
      type: 'error',
    };
  } else if (status === 403) {
    return createCustomOptions('Forbidden', message || 'Unable to access');
  } else if (status > 404 && status < 500 && status !== 409) {
    return createCustomOptions(
      `HTTP error ${status}`,
      message || "There's something wrong with the request",
    );
  } else if (status >= 500) {
    return createCustomOptions('Server error', message || 'Something went wrong.');
  } else if (status === 'FETCH_ERROR') {
    return {
      message: 'Fetch error',
      className: 'extended-notification',
      description: message,
      type: 'error',
    };
  }
  return null;
}

class ErrorHandler extends PureComponent {
  static propTypes = {
    error: PropTypes.object,
    history: PropTypes.object,
    dispatch: PropTypes.func,
    type: PropTypes.oneOf(['alert', 'notification']).isRequired,
    intl: PropTypes.object,
    isFullScreen: PropTypes.bool,
    isWarning: PropTypes.bool,
  };

  static defaultProps = {
    type: 'notification',
    error: {},
  };

  getTranslationKey = (translationKey = null) => {
    const { intl } = this.props;

    return translationKey ?
      intl.formatMessage({ id: translationKey }) :
      intl.formatMessage({ id: 'common.OK' });
  };

  showAlert = ({ message, description, type, retryAction }) => {
    const { dispatch, intl } = this.props;
    window.scrollTo(0, 0);
    return (
      <Alert
        ref={el => (this.alert = el)}
        message={message}
        closable
        closeText={intl.formatMessage({ id: 'common.CLOSE' })}
        description={
          <>
            {description}
            <>
              {retryAction &&
                <ErrorButton
                  onClick={() => dispatch(retryAction)}
                  icon={<SyncOutlined />}
                  className='alert-button'
                >
                  {intl.formatMessage({ id: 'errorhandler.RETRY' })}
                </ErrorButton>
              }
            </>
          </>
        }
        type={type}
        showIcon
      />
    );
  };

  showNotification = (options) => {
    const { dispatch, isFullScreen = false, intl } = this.props;

    const allOptions = {
      ...options,
      description: <>
        {options?.description}
        {options?.retryAction &&
          <div>
            <ErrorButton
              onClick={() => {
                dispatch(options?.retryAction);
                notification.destroy(options?.key);
              }}
              icon={<SyncOutlined />}
              className='alert-button'
            >
              {intl.formatMessage({ id: 'errorhandler.RETRY' })}
            </ErrorButton>
          </div>
        }
      </>,
      onClose: () => {
        notification.destroy(options?.key);
      },
    };
    const notificationOptions = isFullScreen ? {
      ...allOptions,
    } :
      allOptions;
    setTimeout(() => {
      dispatch(showNotification(notificationOptions));
    }, 0);
  };

  handleAction = (message, actions, title, submessage, errors, errortype) => {
    const { history, dispatch } = this.props;

    const description =
      <Description>
        <Section>{message}</Section>
        {errors?.length ?
          <Section>
            <ErrorList>
              {errors?.map((err, i) =>
                <li key={i}>{err}</li>,
              )}
            </ErrorList>
          </Section>
          : null}
        {submessage ? <Section>{submessage}</Section> : null}
      </Description>;

    const key = 'not_found';
    const btn = actions?.length ?
      actions?.map((a, i) => {
        if (a?.id === 'redirect') {
          const uri = a?.uri ? `/${a.uri}` : null;
          const btnClick = () => {
            dispatch(clearNotifications());
            if (uri) {
              history.push(uri);
            }
          };
          return (
            <ErrorButton key={i} onClick={btnClick}>
              {this.getTranslationKey(a?.translationKey)}
            </ErrorButton>
          );
        }
        return <></>;
      })
      :
      <ErrorButton onClick={() => notification.destroy(key)}>
        {this.getTranslationKey()}
      </ErrorButton>;

    const options = {
      message: title || 'An error occured',
      description,
      className: 'extended-notification',
      type: errortype === 'ActionError' ? 'error' : 'warning',
      duration: 0,
      key,
      btn,
    };
    notification.destroy(key);
    this.showNotification(options);
  };

  renderGenericError = ({ title, isWarning, sections, actions, reference }, intl, history) => {
    const options = {
      message: title || 'An error occured',
      description: <GenericError
        sections={sections}
        actions={actions}
        reference={reference}
        close={() => notification.destroy('genericError')}
        intl={intl}
        history={history}
      />,
      className: 'extended-notification',
      type: !isWarning ? 'error' : 'warning',
      duration: 0,
      style: {
        width: 500,
      },
      key: 'genericError',
    };
    this.showNotification(options);
  };

  renderValidationSummary = (errors, actions, backupHistory) =>
    <ErrorSummary
      errors={errors}
      actions={actions}
      backupHistory={backupHistory}
      isValidationError={1}
      extraStyles={errorSummaryStyles}
      isStandAlone={true}
    />;

  render() {
    const { type, error, history, intl } = this.props;

    if (Object.keys(error).length) {
      const {
        actions,
        status,
        message,
        errors,
        retryAction,
        title,
        backupHistory,
        submessage,
        isWarning,
        isNotAuthorized,
      } = error;
      const errortype = error.type;

      if (isNotAuthorized && status === 403) {
        return <ExternalRoutes />;
      }

      if (errortype === 'DynamicError') {
        this.renderGenericError(error, intl, history);
        return null;
      }

      if (status === 400 && errortype === 'validationError') {
        return this.renderValidationSummary(errors, actions, backupHistory);
      }

      if (status === 410) {
        if (actions?.find(action => action.id === 'redirect')) {
          const redirectAction = actions?.find(action => action.id === 'redirect');
          const redirectUrl = decodeURIComponent(history.location.pathname)
            .replace(
              redirectAction.fromId,
              redirectAction.toId,
            );
          history.push(
            redirectUrl,
            { context: redirectAction.toId },
          );
        }
        return null;
      }

      // we always treat 404 and actionerror as a notification since we will redirect
      if (errortype === 'ActionError' || status === 404) {
        this.handleAction(message, actions, title, submessage, errors, errortype);
      }
      const options = getErrorOptions({ status, message, errors, title, isWarning, intl });
      if (errortype !== 'ActionError' && options) {
        if (type === 'alert' && !isWarning) {
          return this.showAlert({ ...options, retryAction });
        }
        this.showNotification({
          ...options,
          key: 'alert_error',
          duration: 0,
          retryAction,
        });
      }
    }
    return null;
  }
}

export default compose(
  withRouter,
  injectIntl,
  connect(),
)(React.memo(ErrorHandler, (p, n) => isEqual(n.error, p.error)));
